import React from "react";
import styled from "styled-components";
import {
  CircularProgress,
  FlatButton,
  RaisedButton,
  TextField
} from "material-ui";
import request from "../../../config/axios-config";
import * as ContractsService from "../../../services/contract-service";
import ContractsDialog from "./ContractsDialog";
import ContractsTable from "./ContractsTable";
import Header from "../../header";
import SearchFilterChip from "../../search/SearchFilterChip";
import { Constants } from "../../../constants";
import * as fileSaver from "file-saver";
import * as validator from "../../../general/validators/validator";
import { connect } from "react-redux";
import * as ErrorAction from "../../../redux/action/error-action";

const Container = styled.div``;

const YearContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  width: 100%;
  margin-bottom: 12px;
`;

const YearSearchContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 4px 8px;
`;

const LoadingContainer = styled.div`
  text-align: center;
`;

class ContractsPage extends React.Component {
  constructor(props) {
    super(props);

    const urlParams = new URLSearchParams(props.location.search);
    const urlYear = parseInt(urlParams.get("year"), 10);

    this.state = {
      loading: false,
      showAddDialog: false,
      showEditDialog: false,
      data: [],
      showEditData: null,
      downloadingReport: false,
      year: isNaN(urlYear) ? new Date().getFullYear() : urlYear,
      searchWords: urlParams.get("q") || ""
    };
  }

  componentDidMount() {
    this._fetchData();
  }

  componentDidUpdate(prevProps) {
    // Re-sync when the URL changes underneath us (global search click-through
    // while this page is mounted). Our own pushes match state, so no-op then.
    if (prevProps.location.search !== this.props.location.search) {
      const urlParams = new URLSearchParams(this.props.location.search);
      const urlYear = parseInt(urlParams.get("year"), 10);
      const year = isNaN(urlYear) ? new Date().getFullYear() : urlYear;
      const query = (urlParams.get("q") || "").trim();
      if (
        year !== parseInt(this.state.year, 10) ||
        query !== this.state.searchWords.trim()
      ) {
        this.setState({ year: year, searchWords: query }, () =>
          this._fetchData()
        );
      }
    }
  }

  _pushUrl = () => {
    let url = "/app/contracts?year=" + this.state.year;
    if (!validator.isEmpty(this.state.searchWords)) {
      url += "&q=" + encodeURIComponent(this.state.searchWords.trim());
    }
    this.props.history.push(url);
  };

  _fetchData = () => {
    this.setState({
      loading: true
    });
    ContractsService.getAllContracts(this.state.year, this.state.searchWords)
      .then(response => {
        this.setState({
          data: response.data,
          loading: false
        });
      })
      .catch(err => {
        this.props.showError("A aparut o eroare. Va rog reincercati!");
        this.setState({
          loading: false
        });
      });
  };

  _onClearSearch = () => {
    this.setState({ searchWords: "" }, () => {
      this._pushUrl();
      this._fetchData();
    });
  };

  onExcelDownloadClick = () => {
    const url =
      Constants.SERVER_ENDPOINT +
      "/document/contracts/excel?year=" +
      this.state.year;
    this.setState({
      downloadingReport: true
    });
    this.props.hideError();
    request({
      url: url,
      method: "GET",
      responseType: "blob"
    })
      .then(response => {
        const suggestedFileName = response.headers.filename;
        const effectiveFileName =
          suggestedFileName === undefined
            ? "Contracte raport.xlsx"
            : suggestedFileName;
        fileSaver.saveAs(response.data, effectiveFileName);

        this.setState({
          downloadingReport: false
        });
      })
      .catch(response => {
        this.setState({
          downloadingReport: false
        });
        this.props.showError(
          "Descarcarea documentului a esuat! Va rog reincercati!"
        );
      });
  };

  render() {
    const {
      loading,
      showAddDialog,
      showEditDialog,
      data,
      year,
      showEditData,
      downloadingReport
    } = this.state;
    return (
      <Container>
        {(showAddDialog || showEditDialog) && (
          <ContractsDialog
            title={showEditDialog ? "Editeaza contract" : "Adauga contract"}
            data={showEditData}
            onCancel={shouldRefresh => {
              this.setState({
                showAddDialog: false,
                showEditDialog: false,
                showEditData: null
              });
              this.props.onDialogVisible(false);
              if (shouldRefresh) {
                this._fetchData();
              }
            }}
          />
        )}

        <Header title={"Contracte"} />

        <YearContainer>
          <YearSearchContainer>
            <TextField
              style={{ width: 80 }}
              floatingLabelText="Anul"
              type={"number"}
              value={year}
              onChange={e => {
                this.setState({ year: e.target.value });
              }}
            />

            <FlatButton
              style={{ marginBottom: 6 }}
              primary
              icon={
                <i
                  className="material-icons"
                  style={{ fontSize: 18, verticalAlign: "middle" }}
                >
                  search
                </i>
              }
              label={"Cauta"}
              onClick={() => {
                this._pushUrl();
                this._fetchData();
              }}
            />

            {/* search comes only from the global bar; this just shows the
                deep-linked term and clears it */}
            <div style={{ marginLeft: 24 }}>
              <SearchFilterChip
                query={(this.state.searchWords || "").trim()}
                onClear={this._onClearSearch}
              />
            </div>
            <FlatButton
              style={{ marginBottom: 6 }}
              primary
              icon={
                <i
                  className="material-icons"
                  style={{ fontSize: 18, verticalAlign: "middle" }}
                >
                  file_download
                </i>
              }
              label={"Descarca raportul"}
              onClick={() => this.onExcelDownloadClick()}
            />

            {downloadingReport && <CircularProgress />}
          </YearSearchContainer>

          <RaisedButton
            style={{ marginBottom: 6 }}
            primary
            label={"Adauga"}
            onClick={() => {
              this.setState({
                showAddDialog: true
              });
              this.props.onDialogVisible(true);
            }}
          />
        </YearContainer>

        {loading && (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        )}

        {data && data.length > 0 ? (
          <ContractsTable
            data={data}
            onDetailsClicked={el => {
              this.props.onDialogVisible(true);
              this.setState({
                showEditDialog: true,
                showEditData: el
              });
            }}
          />
        ) : (
          !loading && (
            <div className="ds-empty">
              <h3>
                {!validator.isEmpty(this.state.searchWords)
                  ? "Niciun contract pentru cautare."
                  : "Nu exista contracte pentru perioada selectata"}
              </h3>
            </div>
          )
        )}
      </Container>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    user: state.login.user
  };
}

function mapDispatchToProps(dispatch) {
  return {
    showError: text => dispatch(ErrorAction.showError(text)),
    hideError: () => dispatch(ErrorAction.hideError())
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ContractsPage);
